import React, { CSSProperties, useMemo, useState, useEffect, useRef } from 'react';
import ReactModal, { Styles } from 'react-modal';

import { NavItem, MenuColumnTheme } from '../interfaces';
import MenuColumn from './MenuColumn';
import { getNavId } from './helpers';
import * as Styled from './styled';

export type ComponentProps = {
  /**
   * Active openMenu state
   */
  openMenu: number[];
  /**
   * Setter for the openMenu
   */
  setOpenMenu: React.Dispatch<React.SetStateAction<number[]>>;
  /**
   * All the items
   */
  items: NavItem[];
  /**
   *  Callback handler when the menu is closed.
   */
  onClose: () => void;
  /**
   * Optional the px height of the header. Offsets the overlay of the dropdown nav menu. Defaults to 80.
   */
  headerHeight?: number;
  /**
   * Identifies the root of the app, which can be set to aria-hidden when the nav is open.
   */
  appElement?: HTMLElement | null;
};

const COLUMN_COUNT = 3;
const COLUMNS = Array.from({ length: COLUMN_COUNT }, (_, i) => i);
const COLUMN_COLORS = [
  MenuColumnTheme.DARK_GREY,
  MenuColumnTheme.BLUE_GREY,
  MenuColumnTheme.BLUE_GREY_75
];

const getNavItems = (items: NavItem[], openMenu: number[]) =>
  COLUMNS.map(colIdx =>
    Array.from({ length: colIdx }, (_, i) => i).reduce(
      (curr, idx) => curr[openMenu[idx + 1]]?.subItems || [],
      items
    )
  );

const SubMenu: React.FC<ComponentProps> = ({
  items,
  openMenu,
  onClose,
  setOpenMenu,
  headerHeight = 100,
  appElement
}) => {
  const [virtualFocusIdx, setVirtualFocusIdx] = useState<number[]>([]);

  const onSelect = (depth: number) => (itemIdx: number) => {
    // Set the next navigation item by item ID
    setVirtualFocusIdx([...openMenu.slice(1, depth), itemIdx]);
    setOpenMenu(menu => [...menu.slice(0, depth), itemIdx]);
  };

  /**
   * navItems is used during the render, whereas the ref to the navItems is used during the keypress
   * - If we just use the ref, it isn't updated in time for the render and it lags behind
   * - If we just use the navItems, the keypress can't get the live value of it
   */
  const navItems = getNavItems(items, openMenu);
  const navItemsRef = useRef(navItems);

  useEffect(() => {
    navItemsRef.current = getNavItems(items, openMenu);
  }, [items, openMenu]);

  const customStyles = useMemo(() => {
    const overlay: CSSProperties = {
      backgroundColor: 'rgba(142, 144, 147, 0.6)',
      top: `${headerHeight}px`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      zIndex: 100
    };
    const content: CSSProperties = {
      position: 'static',
      width: 'calc(100% - 16px)',
      maxWidth: '1482px',
      margin: '8px 8px 0 8px',
      borderRadius: '0',
      border: 'none',
      padding: '0'
    };
    return {
      overlay,
      content
    };
  }, [headerHeight]);
  const isOpen = !!openMenu.length;

  useEffect(() => {
    try {
      if (virtualFocusIdx.length) {
        setOpenMenu(menu => [menu[0], ...virtualFocusIdx]);

        const navId = getNavId(
          virtualFocusIdx.length - 1,
          virtualFocusIdx[virtualFocusIdx.length - 1]
        );
        // Any time the virtual element changes, focus on it
        document.getElementById(navId)?.focus();
      }
    } catch (e) {
      // no-op - if it fails, we just suppress it
    }
  }, [virtualFocusIdx, setOpenMenu]);

  const onKeyPress = React.useCallback((e: KeyboardEvent) => {
    setVirtualFocusIdx(val => {
      const nav = navItemsRef.current;
      const currentIdxHighlighted = val[val.length - 1]; // -> The current index on the active highlighted column
      const totalOptionsInColumn = nav[val.length - 1].length; // -> The total number of options in the highlighted column

      const replaceLast = (newIdx: number) => [...val.slice(0, -1), newIdx];

      const handleDownArrow = () => {
        if (currentIdxHighlighted === totalOptionsInColumn - 1) {
          return replaceLast(0);
        } else {
          return replaceLast(currentIdxHighlighted + 1);
        }
      };

      const handleUpArrow = () => {
        if (currentIdxHighlighted === 0) {
          return replaceLast(totalOptionsInColumn - 1);
        } else {
          return replaceLast(currentIdxHighlighted - 1);
        }
      };

      const handleRightArrow = () => {
        if (!!nav[val.length]?.length) {
          return [...val, 0];
        }
        return val;
      };

      const handleLeftArrow = () => {
        if (val.length > 1) {
          return val.slice(0, -1);
        }
        return val;
      };

      switch (e.key) {
        case 'Tab':
          e.preventDefault();
          e.stopPropagation();
          if (e.shiftKey) {
            return handleUpArrow();
          } else {
            return handleDownArrow();
          }
        case 'PageDown':
        case 'ArrowDown':
          e.preventDefault();
          return handleDownArrow();
        case 'PageUp':
        case 'ArrowUp':
          e.preventDefault();
          return handleUpArrow();
        case 'ArrowRight':
          e.preventDefault();
          return handleRightArrow();
        case 'ArrowLeft':
          e.preventDefault();
          return handleLeftArrow();
        case 'Home':
          e.preventDefault();
          return replaceLast(0);
        case 'End':
          e.preventDefault();
          return replaceLast(totalOptionsInColumn - 1);
        default:
          return val;
      }
    });
  }, []);

  useEffect(() => {
    if (isOpen) {
      // Set index to -1 so nothing is focused. up key will go to end of list, down key will go to 0
      setVirtualFocusIdx([-1]);
      document.addEventListener('keydown', onKeyPress, true);
    } else {
      document.removeEventListener('keydown', onKeyPress, true);
    }
    return () => {
      document.removeEventListener('keydown', onKeyPress, true);
    };
  }, [isOpen, onKeyPress]);

  return (
    <ReactModal
      isOpen={isOpen}
      style={customStyles as Styles}
      onRequestClose={onClose}
      appElement={appElement ?? document.body}>
      <Styled.MenuContent>
        {navItems.map((items, idx) => (
          <MenuColumn
            key={idx}
            colNum={idx}
            activeId={items[openMenu[idx + 1]]?.id}
            items={items}
            onSelect={onSelect(idx + 1)}
            colorScheme={COLUMN_COLORS[idx]}
          />
        ))}
      </Styled.MenuContent>
    </ReactModal>
  );
};

export default SubMenu;
